import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeUp, staggerParent } from "@/lib/motion";

const TERMLY_IDS = {
  privacy: "5deeef70-73df-4f59-a681-30aee950f4dc",
  terms: "217a5086-bbdf-43e1-80f0-9b84d61b4508",
};
const TERMLY_SCRIPT_ID = "termly-embed-policy-script";

export default function Legal() {
  const loc = useLocation();
  const isTerms = loc.pathname.includes("/terms");
  const label = isTerms ? "Terms of Service" : "Privacy Policy";
  const overline = isTerms ? "Terms" : "Privacy";
  const termlyId = isTerms ? TERMLY_IDS.terms : TERMLY_IDS.privacy;

  useEffect(() => {
    // Inject the Termly embed loader once. It scans the page for
    // <div name="termly-embed" data-id="..."> and renders the policy.
    if (!document.getElementById(TERMLY_SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = TERMLY_SCRIPT_ID;
      s.src = "https://app.termly.io/embed-policy.min.js";
      s.async = true;
      document.body.appendChild(s);
    } else if (window.Termly && typeof window.Termly.initialize === "function") {
      // Re-scan if the loader is already present (route change)
      window.Termly.initialize();
    }
  }, [loc.pathname]);

  return (
    <div data-testid="legal-page" className="pt-24 sm:pt-32 overflow-hidden">
      <section className="relative py-12 sm:py-20 grain">
        <div className="container-x relative">
          <motion.div
            initial="hidden"
            animate="show"
            variants={staggerParent}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} className="text-overline">
              {overline}
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="mt-4 font-display text-5xl sm:text-7xl text-ink tracking-[-0.03em] leading-[0.95]"
            >
              {label}.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-6 text-lg text-ink-2 leading-relaxed max-w-2xl"
            >
              The document below is generated and kept up to date by Termly.
              It reflects our current {label.toLowerCase()} and how we handle
              your data.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-8 sm:py-12 border-t border-ink">
        <div className="container-x">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-ink bg-white p-6 sm:p-10"
          >
            {/*
              Termly-managed policy embed. The remote script scans for divs
              with name="termly-embed" and injects the policy content here.
            */}
            <div
              name="termly-embed"
              data-id={termlyId}
              key={termlyId}
              data-testid="termly-embed"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
